/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect } from 'react';

import { css, jsx } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, Stack, xcss } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { ContentContainer } from '../../../../common/ui/content-container';
import { isBreakpointActive, useElementWidth } from '../../../../common/ui/width-observer';

type BodyProps = {
	components: {
		results: JSX.Element;
		smartAnswers: JSX.Element;
		oauthContainer: JSX.Element;
		rightPanel?: JSX.Element;
		autoCorrection: JSX.Element;
		messageBanner?: JSX.Element;
	};
};

const RightPanelContainerStyles = xcss({
	width: '100%',
	display: 'inline',
});

const bodyStackStyles = xcss({
	// By default, elements with display: flex will have a width of auto, which
	// means they will not shrink below the width of their content. By setting
	// this to 0, it ensures that this stack will not grow beyond its width.
	minWidth: '0',
});

const gridStyles = xcss({
	grid: '1fr / 100fr',
});

const gridStylesSmall = xcss({
	grid: '1fr / 70fr 30fr',
});

const gridStylesMedium = xcss({
	grid: '1fr / 100fr 250px',
});

// Prevent the smart answers dialog from z-indexing above the dialog, filters etc
const smartAnswersWrapperStyles = css({
	zIndex: 0,
	maxWidth: '838px',
});

export const BodyContent = ({ components }: BodyProps) => {
	const { smartAnswers, results: resultsComponent, autoCorrection, messageBanner } = components;

	useEffect(() => {
		// The scroll is on the window level so we have to scroll window to 0,0 to reset the scroller
		window.scrollTo(0, 0);
	}, [smartAnswers, resultsComponent]);

	return (
		<div css={smartAnswersWrapperStyles}>
			<Stack space="space.200" xcss={bodyStackStyles}>
				{autoCorrection}
				{smartAnswers}
				{messageBanner}
				{resultsComponent}
			</Stack>
		</div>
	);
};

/**
 * Body
 * Controls the layout of the search page "below the fold":
 * Including how to show the [OAuth, SAIN, Results] and/or a RightPanel
 *
 * The RightPanel is only shown when the viewport is >breakpoints.sm
 */
export const Body = ({ components }: BodyProps) => {
	const { rightPanel } = components;

	const width = useElementWidth('layout');

	// Primary use case for this is side-by-side which does not set a rightPanel
	if (!rightPanel) {
		return (
			<ContentContainer>
				<Grid gap="space.400" templateColumns={'100fr'}>
					<BodyContent components={components} />
				</Grid>
			</ContentContainer>
		);
	} else if (width > 0) {
		const isAboveMedium = isBreakpointActive({ width, above: 'md' });
		const isAboveSmall = isBreakpointActive({ width, above: 'sm' });
		let className = gridStyles;
		if (isAboveMedium) {
			className = gridStylesMedium;
		} else if (isAboveSmall) {
			className = gridStylesSmall;
		}
		return (
			<ContentContainer>
				<Grid gap="space.1000" xcss={className}>
					<BodyContent components={components} />
					{isAboveSmall && <Box xcss={RightPanelContainerStyles}>{rightPanel}</Box>}
				</Grid>
			</ContentContainer>
		);
	}

	return <UFOLoadHold name="search-page-body-layout-width" />;
};
